import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ClipboardList, FilePlus2, Upload, BookOpen, Users, LogOut, Menu, Factory, Bell, PanelLeftClose, PanelLeftOpen, DollarSign, Briefcase
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ROLE_LABELS } from "@/constants";

const SIDEBAR_HIDDEN_KEY = "ot_sidebar_hidden";

const navItemsAll = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "sidebar-nav-dashboard", perm: "dashboard" },
  { to: "/ots", label: "Órdenes de Trabajo", icon: ClipboardList, testId: "sidebar-nav-ots", perm: "ots" },
  { to: "/ots/new", label: "Nueva OT", icon: FilePlus2, testId: "sidebar-nav-ot-new", perm: "ot_create" },
  { to: "/ots/upload", label: "Cargar Excel", icon: Upload, testId: "sidebar-nav-ot-upload", perm: "ot_upload" },
  { to: "/catalogs", label: "Catálogos", icon: BookOpen, testId: "sidebar-nav-catalogs", perm: "catalogs" },
  { to: "/costos", label: "Costos Maquila", icon: DollarSign, testId: "sidebar-nav-costos", perm: "costos_view" },
  { to: "/ots-comercial", label: "OTs Comercial", icon: Briefcase, testId: "sidebar-nav-otac", perm: "otac_view" },
  { to: "/users", label: "Usuarios", icon: Users, testId: "sidebar-nav-users", perm: "users" },
  { to: "/notifications", label: "Notificaciones", icon: Bell, testId: "sidebar-nav-notifications", perm: "users" },
];

function NavList({ onItemClick }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const perms = user?.permissions || [];
  const items = navItemsAll.filter((i) => isAdmin || perms.includes(i.perm));
  return (
    <nav className="flex flex-col gap-1 px-3 py-3">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            onClick={onItemClick}
            end={it.to === "/dashboard"}
            data-testid={it.testId}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-md px-3 py-3 sm:py-2 text-sm transition-colors duration-150 min-h-[44px] ` +
              (isActive
                ? "bg-muted/50 text-foreground shadow-[inset_3px_0_0_hsl(var(--primary))]"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/40 active:bg-muted/60")
            }
          >
            <Icon className="h-5 w-5 sm:h-4 sm:w-4" />
            <span className="truncate">{it.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-11 gap-2 px-2" data-testid="user-menu-trigger">
          <div className="h-8 w-8 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center text-xs font-semibold text-primary">
            {user?.username?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="hidden sm:flex flex-col items-start text-left">
            <span className="text-sm leading-tight">{user?.full_name || user?.username}</span>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{ROLE_LABELS[user?.role] || user?.role}</span>
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Sesión</DropdownMenuLabel>
        <DropdownMenuItem disabled>{user?.username}</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="user-logout-button"
          onClick={() => {
            logout();
            navigate("/login");
          }}
        >
          <LogOut className="h-4 w-4 mr-2" /> Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AppLayout() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_HIDDEN_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_HIDDEN_KEY, sidebarHidden ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [sidebarHidden]);

  const toggleDesktopSidebar = () => setSidebarHidden((v) => !v);

  return (
    <div
      className={
        `min-h-screen grid grid-cols-1 bg-background text-foreground ` +
        (sidebarHidden ? "lg:grid-cols-1" : "lg:grid-cols-[260px_1fr]")
      }
      data-sidebar-hidden={sidebarHidden ? "true" : "false"}
    >
      {/* Sidebar (desktop) */}
      {!sidebarHidden && (
        <aside
          className="hidden lg:flex flex-col border-r border-border bg-card/60 sticky top-0 h-screen"
          data-testid="desktop-sidebar"
        >
          <div className="h-14 flex items-center gap-2 px-4 border-b border-border">
            <div className="h-8 w-8 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center">
              <Factory className="h-4 w-4 text-primary" />
            </div>
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-sm font-semibold tracking-tight">OT Maquila</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Operaciones</span>
            </div>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    onClick={toggleDesktopSidebar}
                    aria-label="Ocultar menú lateral"
                    data-testid="desktop-sidebar-hide-button"
                  >
                    <PanelLeftClose className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Ocultar menú</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex-1 overflow-y-auto">
            <NavList />
          </div>
          <div className="border-t border-border px-4 py-3 text-[10px] text-muted-foreground">
            v1.0  •  Dark mode
          </div>
        </aside>
      )}

      {/* Main */}
      <div className="flex flex-col min-h-screen">
        <header
          className="border-b border-border bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60 flex items-center justify-between px-3 sm:px-4 lg:px-6 sticky top-0 z-30 pt-[env(safe-area-inset-top,0px)]"
          style={{ minHeight: "calc(3.5rem + env(safe-area-inset-top, 0px))" }}
        >
          <div className="flex items-center gap-2 min-w-0 h-14">
            {/* Mobile sidebar (Sheet) */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden h-11 w-11 -ml-2"
                  data-testid="mobile-sidebar-trigger"
                  aria-label="Abrir menú de navegación"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-[85vw] max-w-[320px]">
                <div className="h-14 flex items-center gap-2 px-4 border-b border-border">
                  <div className="h-8 w-8 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center">
                    <Factory className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold">OT Maquila</span>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Operaciones</span>
                  </div>
                </div>
                <div className="overflow-y-auto max-h-[calc(100vh-3.5rem)]">
                  <NavList onItemClick={() => setMobileNavOpen(false)} />
                </div>
              </SheetContent>
            </Sheet>

            {/* Desktop show-sidebar button (only visible when hidden) */}
            {sidebarHidden && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="hidden lg:inline-flex h-9 w-9 -ml-1 text-muted-foreground hover:text-foreground"
                      onClick={toggleDesktopSidebar}
                      aria-label="Mostrar menú lateral"
                      data-testid="desktop-sidebar-show-button"
                    >
                      <PanelLeftOpen className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Mostrar menú</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Mobile brand */}
            <div className="lg:hidden flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center shrink-0">
                <Factory className="h-3.5 w-3.5 text-primary" />
              </div>
              <span className="text-sm font-semibold tracking-tight truncate">OT Maquila</span>
            </div>

            {/* Desktop brand when sidebar hidden */}
            {sidebarHidden && (
              <div className="hidden lg:flex items-center gap-2 min-w-0">
                <div className="h-7 w-7 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center shrink-0">
                  <Factory className="h-3.5 w-3.5 text-primary" />
                </div>
                <span className="text-sm font-semibold tracking-tight">OT Maquila</span>
              </div>
            )}

            <div className="hidden sm:block">
              <h1 className="text-sm font-medium tracking-tight text-muted-foreground">Panel de control</h1>
            </div>
          </div>
          <UserMenu />
        </header>
        <main
          className={
            `flex-1 px-3 sm:px-4 lg:px-6 py-4 w-full mx-auto pb-[calc(1rem+env(safe-area-inset-bottom,0px))] ` +
            (sidebarHidden ? "max-w-[1600px]" : "max-w-[1400px]")
          }
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
